/**
 * ZelloPanel — Phase 8 (Wave 2b).
 *
 * Renders inside the Customers page's per-customer detail row, after the
 * existing SubscriptionPanel + DevicesPanel cards. Three sub-cards:
 *
 *   1. Plan summary  — plan badge, status, period end, user/channel counts,
 *                      Stripe subscription id.
 *   2. Channels      — table + inline "+ Add channel" form.
 *   3. Users         — table + inline "+ Add device" form (the operator
 *                      flow for adding extra Pi devices to an existing
 *                      Zello sub without going through Stripe Checkout).
 *
 * All mutations go through `lib/api.ts` and on success invalidate both
 * `["customer-zello", customer.id]` (so this panel snaps to truth) and
 * `["devices"]` (so the DeviceDetail Zello card and the customer's
 * linked-devices panel update in lockstep).
 *
 * Design notes:
 *   - Multi-channel selection on the "+ Add device" form is a checkbox
 *     list rather than a `<select multiple>` — checkboxes scale better to
 *     a handful of channels and don't require ⌘-click on macOS, which
 *     racing-pit operators using locked-down kiosk-style admin tablets
 *     have repeatedly tripped over in user testing of similar UIs.
 *   - Destructive deletes always go through `window.confirm()` because
 *     they hit the Zello Work API + decrement the Stripe subscription
 *     quantity — they are not cheaply reversible.
 */

import { useEffect, useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import { EmptyBlock, ErrorBlock, LoadingBlock } from "@/components/StatusStates";
import {
  addZelloChannel,
  addZelloDevice,
  deleteZelloChannel,
  deleteZelloUser,
  getCustomerZello,
  resendZelloCredentials,
} from "@/lib/api";
import { formatDateTime, formatRelative } from "@/lib/format";
import type {
  CustomerZelloState,
  Customer,
  Device,
  ZelloChannel,
  ZelloUser,
} from "@/lib/types";

// ---------------------------------------------------------------------------
// Top-level panel
// ---------------------------------------------------------------------------

export default function ZelloPanel({
  customer,
  customerDevices,
  allDevices,
}: {
  customer: Customer;
  /** Devices already linked to this customer — first-class candidates for add-device. */
  customerDevices: Device[];
  /** Full fleet — used to surface unlinked devices that the operator can pull in. */
  allDevices: Device[];
}) {
  const queryClient = useQueryClient();
  const stateQ = useQuery({
    queryKey: ["customer-zello", customer.id],
    queryFn: () => getCustomerZello(customer.id),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["customer-zello", customer.id] });
    queryClient.invalidateQueries({ queryKey: ["devices"] });
  };

  return (
    <Card title="Zello voice" subtitle="Phase 8 — voice + bundle plans">
      <ZelloPanelBody
        customer={customer}
        customerDevices={customerDevices}
        allDevices={allDevices}
        stateQ={stateQ}
        onMutated={invalidate}
      />
    </Card>
  );
}

function ZelloPanelBody({
  customer,
  customerDevices,
  allDevices,
  stateQ,
  onMutated,
}: {
  customer: Customer;
  customerDevices: Device[];
  allDevices: Device[];
  stateQ: UseQueryResult<CustomerZelloState, unknown>;
  onMutated: () => void;
}) {
  if (stateQ.isLoading) return <LoadingBlock label="Loading Zello state…" />;
  if (stateQ.isError) {
    return (
      <ErrorBlock error={stateQ.error} onRetry={() => stateQ.refetch()} />
    );
  }
  const state = stateQ.data;
  if (!state || !state.subscription) {
    return (
      <EmptyBlock
        title="No Zello subscription on this customer yet."
        description="Customers get a Zello sub by activating a device on the Voice or Bundle plan, or via the Bundle add-on in admin once Stripe Products are configured."
      />
    );
  }

  return (
    <div className="space-y-5">
      <PlanSummary subscription={state.subscription} />
      <ChannelsCard
        customerId={customer.id}
        channels={state.channels}
        onMutated={onMutated}
      />
      <UsersCard
        customerId={customer.id}
        users={state.users}
        channels={state.channels}
        userChannelMap={state.user_channel_map}
        customerDevices={customerDevices}
        allDevices={allDevices}
        onMutated={onMutated}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plan summary sub-card
// ---------------------------------------------------------------------------

function PlanSummary({
  subscription,
}: {
  subscription: NonNullable<CustomerZelloState["subscription"]>;
}) {
  return (
    <section className="rounded border border-surface-border bg-surface-sunken/60 p-4">
      <div className="flex items-center gap-3 flex-wrap">
        <PlanBadge plan={subscription.plan} />
        <SubStatusBadge status={subscription.status} />
        {subscription.cancel_at_period_end ? (
          <span className="text-[11px] text-amber-300 uppercase tracking-wider">
            cancels at period end
          </span>
        ) : null}
      </div>
      <dl className="mt-3 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2 lg:grid-cols-3 text-sm">
        <Field label="Users">
          <span className="text-slate-100">{subscription.user_count}</span>
        </Field>
        <Field label="Channels">
          <span className="text-slate-100">{subscription.channel_count}</span>
        </Field>
        <Field label="Period end">
          <span
            className="text-slate-200"
            title={subscription.current_period_end ?? ""}
          >
            {subscription.current_period_end
              ? formatDateTime(subscription.current_period_end)
              : "—"}
          </span>
        </Field>
        <Field label="Stripe sub" mono>
          <span className="text-slate-300 break-all">
            {subscription.stripe_subscription_id}
          </span>
        </Field>
        <Field label="Created">
          <span
            className="text-slate-300 text-xs"
            title={subscription.created_at}
          >
            {formatRelative(subscription.created_at)}
          </span>
        </Field>
        <Field label="Updated">
          <span
            className="text-slate-300 text-xs"
            title={subscription.updated_at}
          >
            {formatRelative(subscription.updated_at)}
          </span>
        </Field>
      </dl>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Channels sub-card
// ---------------------------------------------------------------------------

function ChannelsCard({
  customerId,
  channels,
  onMutated,
}: {
  customerId: string;
  channels: ZelloChannel[];
  onMutated: () => void;
}) {
  const [friendlyName, setFriendlyName] = useState("");
  const [subscribeExisting, setSubscribeExisting] = useState(true);

  const addMutation = useMutation({
    mutationFn: (inputs: { friendly_name: string; subscribe_existing_users: boolean }) =>
      addZelloChannel(customerId, inputs),
    onSuccess: () => {
      setFriendlyName("");
      setSubscribeExisting(true);
      onMutated();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (channelId: string) => deleteZelloChannel(customerId, channelId),
    onSuccess: onMutated,
  });

  return (
    <section className="rounded border border-surface-border bg-surface-sunken/40 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-100">
          Channels
        </h3>
        <span className="text-[11px] text-brand-dim">
          {channels.length} provisioned
        </span>
      </div>

      {channels.length === 0 ? (
        <p className="text-sm text-brand-dim">
          No channels yet. The first add becomes the default.
        </p>
      ) : (
        <div className="overflow-x-auto -mx-4 -my-2">
          <table className="w-full text-sm">
            <thead className="bg-surface-raised text-left text-[11px] uppercase tracking-wider text-brand-dim">
              <tr>
                <th className="px-4 py-2 font-medium">Zello channel</th>
                <th className="px-4 py-2 font-medium">Friendly name</th>
                <th className="px-4 py-2 font-medium">Default</th>
                <th className="px-4 py-2 font-medium">Created</th>
                <th className="px-4 py-2 font-medium text-right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {channels.map((ch) => (
                <tr key={ch.id} className="hover:bg-surface-raised/40">
                  <td className="px-4 py-2 font-mono text-xs text-slate-100">
                    {ch.zello_channel_name}
                  </td>
                  <td className="px-4 py-2 text-slate-200">
                    {ch.friendly_name ?? (
                      <span className="text-brand-dim">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {ch.is_default ? (
                      <span className="inline-flex items-center rounded-full bg-brand-orange/15 text-orange-300 ring-1 ring-inset ring-brand-orange/40 px-2 py-0.5 text-[11px] uppercase tracking-wider">
                        Default
                      </span>
                    ) : (
                      <span className="text-brand-dim text-xs">—</span>
                    )}
                  </td>
                  <td
                    className="px-4 py-2 text-xs text-slate-300"
                    title={ch.created_at}
                  >
                    {formatRelative(ch.created_at)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => {
                        if (ch.is_default) {
                          window.alert(
                            "Refusing to delete the default channel. Mark another channel default first (server-side flow not yet exposed) or contact engineering.",
                          );
                          return;
                        }
                        if (
                          !window.confirm(
                            `Delete Zello channel "${ch.zello_channel_name}"? This unsubscribes every user from it on Zello Work and decrements the Stripe channel quantity.`,
                          )
                        ) {
                          return;
                        }
                        deleteMutation.mutate(ch.id);
                      }}
                      disabled={deleteMutation.isPending || ch.is_default}
                      className="inline-flex items-center rounded border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[11px] uppercase tracking-wider text-rose-200 hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteMutation.isError ? (
        <ErrorBlock error={deleteMutation.error} />
      ) : null}

      <form
        className="flex flex-wrap items-end gap-3 pt-2 border-t border-surface-border"
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = friendlyName.trim();
          if (!trimmed || addMutation.isPending) return;
          addMutation.mutate({
            friendly_name: trimmed,
            subscribe_existing_users: subscribeExisting,
          });
        }}
      >
        <div className="flex flex-col gap-1 grow min-w-[220px]">
          <label className="text-[11px] uppercase tracking-wider text-brand-dim">
            New channel friendly name
          </label>
          <input
            value={friendlyName}
            onChange={(e) => setFriendlyName(e.target.value)}
            placeholder="e.g. Pit, Driver, Spotter"
            className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
            autoComplete="off"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-200 select-none pt-5">
          <input
            type="checkbox"
            checked={subscribeExisting}
            onChange={(e) => setSubscribeExisting(e.target.checked)}
            className="h-4 w-4 rounded border-surface-border bg-surface-sunken text-brand-orange"
          />
          Subscribe existing users
        </label>
        <button
          type="submit"
          disabled={!friendlyName.trim() || addMutation.isPending}
          className="inline-flex items-center rounded bg-brand-orange px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-orange-400 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {addMutation.isPending ? "Adding…" : "+ Add channel"}
        </button>
      </form>
      {addMutation.isError ? <ErrorBlock error={addMutation.error} /> : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Users sub-card
// ---------------------------------------------------------------------------

function UsersCard({
  customerId,
  users,
  channels,
  userChannelMap,
  customerDevices,
  allDevices,
  onMutated,
}: {
  customerId: string;
  users: ZelloUser[];
  channels: ZelloChannel[];
  userChannelMap: Record<string, string[]>;
  customerDevices: Device[];
  allDevices: Device[];
  onMutated: () => void;
}) {
  const channelById = useMemo(() => {
    const map: Record<string, ZelloChannel> = {};
    for (const ch of channels) map[ch.id] = ch;
    return map;
  }, [channels]);

  const resendMutation = useMutation({
    mutationFn: (userId: string) => resendZelloCredentials(customerId, userId),
    onSuccess: onMutated,
  });
  const deleteMutation = useMutation({
    mutationFn: (userId: string) => deleteZelloUser(customerId, userId),
    onSuccess: onMutated,
  });

  const addDeviceMutation = useMutation({
    mutationFn: (inputs: { device_serial: string; channel_ids: string[] }) =>
      addZelloDevice(customerId, inputs),
    onSuccess: onMutated,
  });

  // Devices we can add to this customer's Zello sub: any device already
  // linked to the customer that doesn't already have a `zello_username`,
  // plus any unlinked-to-anyone device (the backend will auto-link).
  // We exclude devices linked to OTHER customers — those would have to be
  // unlinked via the existing customer-detail Unlink flow first.
  const linkedSerials = useMemo(
    () => new Set(users.filter((u) => u.device_serial).map((u) => u.device_serial!)),
    [users],
  );
  const eligibleDevices = useMemo(() => {
    const merged: Device[] = [];
    const seen = new Set<string>();
    for (const d of customerDevices) {
      if (seen.has(d.serial)) continue;
      if (linkedSerials.has(d.serial)) continue;
      merged.push(d);
      seen.add(d.serial);
    }
    for (const d of allDevices) {
      if (seen.has(d.serial)) continue;
      if (d.customer_id) continue;
      if (linkedSerials.has(d.serial)) continue;
      merged.push(d);
      seen.add(d.serial);
    }
    return merged;
  }, [customerDevices, allDevices, linkedSerials]);

  return (
    <section className="rounded border border-surface-border bg-surface-sunken/40 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-100">
          Users
        </h3>
        <span className="text-[11px] text-brand-dim">
          {users.length} provisioned
        </span>
      </div>

      {users.length === 0 ? (
        <p className="text-sm text-brand-dim">
          No Zello users yet. Provisioning runs automatically on{" "}
          <code>customer.subscription.created</code> for voice/bundle plans.
        </p>
      ) : (
        <div className="overflow-x-auto -mx-4 -my-2">
          <table className="w-full text-sm">
            <thead className="bg-surface-raised text-left text-[11px] uppercase tracking-wider text-brand-dim">
              <tr>
                <th className="px-4 py-2 font-medium">Username</th>
                <th className="px-4 py-2 font-medium">Full name</th>
                <th className="px-4 py-2 font-medium">Kind</th>
                <th className="px-4 py-2 font-medium">Channels</th>
                <th className="px-4 py-2 font-medium">Device / email</th>
                <th className="px-4 py-2 font-medium text-right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {users.map((u) => {
                const assignedIds = userChannelMap[u.id] ?? [];
                const assigned = assignedIds
                  .map((id) => channelById[id])
                  .filter(Boolean) as ZelloChannel[];
                const isOperator = u.kind === "operator";
                const isDevice = u.kind === "device";
                return (
                  <tr key={u.id} className="hover:bg-surface-raised/40">
                    <td className="px-4 py-2 font-mono text-xs text-slate-100">
                      {u.zello_username}
                    </td>
                    <td className="px-4 py-2 text-slate-200">
                      {u.full_name ?? <span className="text-brand-dim">—</span>}
                    </td>
                    <td className="px-4 py-2">
                      <UserKindBadge kind={u.kind} />
                    </td>
                    <td className="px-4 py-2">
                      {assigned.length === 0 ? (
                        <span className="text-brand-dim text-xs">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {assigned.map((ch) => (
                            <span
                              key={ch.id}
                              title={ch.zello_channel_name}
                              className="inline-flex items-center rounded bg-brand-teal/15 text-cyan-200 ring-1 ring-inset ring-brand-teal/40 px-1.5 py-0.5 text-[11px]"
                            >
                              {ch.friendly_name ?? ch.zello_channel_name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-300">
                      {isDevice ? (
                        u.device_serial ? (
                          <code className="rounded bg-surface px-1.5 py-0.5">
                            {u.device_serial}
                          </code>
                        ) : (
                          <span className="text-brand-dim">no device</span>
                        )
                      ) : isOperator ? (
                        u.email_sent_at ? (
                          <span
                            className="text-emerald-300"
                            title={u.email_sent_at}
                          >
                            emailed {formatRelative(u.email_sent_at)}
                          </span>
                        ) : (
                          <span className="text-amber-300">not sent</span>
                        )
                      ) : (
                        <span className="text-brand-dim">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        {isOperator ? (
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                !window.confirm(
                                  `Re-send Zello credentials to ${u.full_name ?? u.zello_username}? A fresh password will be generated and the previous one will stop working.`,
                                )
                              ) {
                                return;
                              }
                              resendMutation.mutate(u.id);
                            }}
                            disabled={resendMutation.isPending}
                            className="inline-flex items-center rounded border border-brand-teal/40 bg-brand-teal/15 px-2 py-1 text-[11px] uppercase tracking-wider text-cyan-200 hover:bg-brand-teal/25 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Resend creds
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => {
                            const msg = isDevice
                              ? `Delete device user ${u.zello_username}?\n\nThis fails server-side until the operator unlinks the device (${u.device_serial ?? "?"}) from the customer first.`
                              : `Delete Zello user ${u.zello_username}? This calls user/delete on Zello Work and decrements the Stripe user quantity. The operator will lose access immediately.`;
                            if (!window.confirm(msg)) return;
                            deleteMutation.mutate(u.id);
                          }}
                          disabled={deleteMutation.isPending}
                          className="inline-flex items-center rounded border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[11px] uppercase tracking-wider text-rose-200 hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {resendMutation.isError ? (
        <ErrorBlock error={resendMutation.error} />
      ) : null}
      {deleteMutation.isError ? (
        <ErrorBlock error={deleteMutation.error} />
      ) : null}

      <AddDeviceForm
        eligibleDevices={eligibleDevices}
        channels={channels}
        onSubmit={(inputs) => addDeviceMutation.mutate(inputs)}
        isPending={addDeviceMutation.isPending}
      />
      {addDeviceMutation.isError ? (
        <ErrorBlock error={addDeviceMutation.error} />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// "+ Add device" form (multi-channel checkbox list)
// ---------------------------------------------------------------------------

function AddDeviceForm({
  eligibleDevices,
  channels,
  onSubmit,
  isPending,
}: {
  eligibleDevices: Device[];
  channels: ZelloChannel[];
  onSubmit: (inputs: { device_serial: string; channel_ids: string[] }) => void;
  isPending: boolean;
}) {
  const [pickedSerial, setPickedSerial] = useState("");
  // Default-pick the channels that are flagged `is_default`. Operators can
  // un-tick if they want a more restricted set.
  const defaultChannelIds = useMemo(
    () => channels.filter((c) => c.is_default).map((c) => c.id),
    [channels],
  );
  const [pickedChannels, setPickedChannels] = useState<string[]>(defaultChannelIds);

  // Re-sync the pre-selected default channels if `channels` changes (e.g. the
  // operator just added a new channel and the panel refetched). We only fill
  // when the current selection is empty so we don't clobber the operator's
  // explicit ticks mid-form.
  useEffect(() => {
    setPickedChannels((prev) => {
      if (prev.length > 0) return prev;
      return defaultChannelIds;
    });
  }, [defaultChannelIds]);

  const toggleChannel = (id: string) => {
    setPickedChannels((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const canSubmit =
    pickedSerial.length > 0 && pickedChannels.length > 0 && !isPending;

  return (
    <form
      className="rounded border border-surface-border bg-surface/50 p-3 space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onSubmit({ device_serial: pickedSerial, channel_ids: pickedChannels });
        setPickedSerial("");
        setPickedChannels(defaultChannelIds);
      }}
    >
      <div className="text-[11px] uppercase tracking-wider text-brand-dim">
        Add device to this Zello sub
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase tracking-wider text-brand-dim">
            Device
          </label>
          <select
            value={pickedSerial}
            onChange={(e) => setPickedSerial(e.target.value)}
            className="bg-surface-sunken border border-surface-border rounded px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand-orange"
          >
            <option value="">Pick a device…</option>
            {eligibleDevices.map((d) => (
              <option key={d.serial} value={d.serial}>
                {(d.device_label ?? d.serial) +
                  (d.device_label ? ` · ${d.serial}` : "") +
                  (d.customer_id ? "" : " (unlinked)")}
              </option>
            ))}
          </select>
          <span className="text-[11px] text-brand-dim leading-snug">
            Already-linked devices without a Zello user, or any unlinked
            device. Linked-to-other-customer devices are excluded.
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wider text-brand-dim">
            Channels (one or more)
          </span>
          <div className="rounded border border-surface-border bg-surface-sunken px-2 py-1.5 max-h-32 overflow-y-auto">
            {channels.length === 0 ? (
              <span className="text-[11px] text-brand-dim">
                No channels available — add one above first.
              </span>
            ) : (
              <ul className="space-y-1">
                {channels.map((ch) => (
                  <li key={ch.id}>
                    <label className="flex items-center gap-2 text-sm text-slate-200 select-none">
                      <input
                        type="checkbox"
                        checked={pickedChannels.includes(ch.id)}
                        onChange={() => toggleChannel(ch.id)}
                        className="h-4 w-4 rounded border-surface-border bg-surface-sunken text-brand-orange"
                      />
                      <span className="font-mono text-xs">
                        {ch.zello_channel_name}
                      </span>
                      {ch.friendly_name ? (
                        <span className="text-brand-dim text-xs">
                          {ch.friendly_name}
                        </span>
                      ) : null}
                      {ch.is_default ? (
                        <span className="text-orange-300 text-[10px] uppercase tracking-wider">
                          default
                        </span>
                      ) : null}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex items-center rounded bg-brand-orange px-3 py-1.5 text-sm font-medium text-slate-900 hover:bg-orange-400 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isPending ? "Adding device…" : "+ Add device"}
        </button>
        <span className="text-[11px] text-brand-dim leading-snug">
          Bumps Stripe user quantity, provisions a Zello device user, and
          pushes credentials to the Pi on its next subscription poll.
        </span>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Badges + small helpers (kept local to avoid bloating shared components)
// ---------------------------------------------------------------------------

function PlanBadge({ plan }: { plan: string }) {
  if (plan === "voice") {
    return (
      <span className="inline-flex items-center rounded-full bg-brand-teal/15 text-cyan-200 ring-1 ring-inset ring-brand-teal/40 px-2 py-0.5 text-xs uppercase tracking-wider">
        Voice
      </span>
    );
  }
  if (plan === "bundle") {
    return (
      <span className="inline-flex items-center rounded-full bg-brand-orange/15 text-orange-300 ring-1 ring-inset ring-brand-orange/40 px-2 py-0.5 text-xs uppercase tracking-wider">
        Bundle
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-surface-sunken text-slate-200 ring-1 ring-inset ring-surface-border px-2 py-0.5 text-xs uppercase tracking-wider">
      {plan || "unknown"}
    </span>
  );
}

function SubStatusBadge({ status }: { status: string }) {
  let cls =
    "bg-surface-sunken text-slate-200 ring-1 ring-inset ring-surface-border";
  switch (status) {
    case "active":
    case "trialing":
      cls =
        "bg-emerald-500/15 text-emerald-300 ring-1 ring-inset ring-emerald-500/30";
      break;
    case "past_due":
    case "unpaid":
      cls =
        "bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/30";
      break;
    case "canceled":
    case "incomplete_expired":
      cls = "bg-rose-500/15 text-rose-300 ring-1 ring-inset ring-rose-500/30";
      break;
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${cls}`}
    >
      {status}
    </span>
  );
}

function UserKindBadge({ kind }: { kind: string }) {
  if (kind === "device") {
    return (
      <span className="inline-flex items-center rounded-full bg-brand-orange/15 text-orange-300 ring-1 ring-inset ring-brand-orange/40 px-2 py-0.5 text-[11px] uppercase tracking-wider">
        Device
      </span>
    );
  }
  if (kind === "operator") {
    return (
      <span className="inline-flex items-center rounded-full bg-brand-teal/15 text-cyan-200 ring-1 ring-inset ring-brand-teal/40 px-2 py-0.5 text-[11px] uppercase tracking-wider">
        Operator
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-surface-sunken text-slate-200 ring-1 ring-inset ring-surface-border px-2 py-0.5 text-[11px] uppercase tracking-wider">
      {kind}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Card primitive (mirrors Customers/DeviceDetail styling exactly)
// ---------------------------------------------------------------------------

function Card({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-surface-border bg-surface">
      <header className="flex items-center justify-between border-b border-surface-border px-5 py-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-100">
          {title}
        </h2>
        {subtitle ? (
          <span className="text-xs text-brand-dim">{subtitle}</span>
        ) : null}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

function Field({
  label,
  children,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <dt className="text-[11px] uppercase tracking-wider text-brand-dim">
        {label}
      </dt>
      <dd
        className={[
          "text-sm text-slate-100 truncate",
          mono ? "font-mono text-xs" : "",
        ].join(" ")}
      >
        {children}
      </dd>
    </div>
  );
}
